import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Excel export fonksiyonu
export const exportToExcel = (data: any[], filename: string, sheetName: string = 'Sheet1') => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

/**
 * Production-style location breakdown export.
 *
 * Mirrors the layout of the reference PDF the user works from:
 *   - one merged group-header row per location ("BAHAR EV — 18.25")
 *   - then the table header row
 *   - then per-scene rows for that group
 *   - blank spacer row, next group, ...
 *
 * `headers` are the visible column names (the "Mekan Grubu" column is
 * stripped before this is called); `groups` are the consecutive scene
 * blocks already grouped by location with their accumulated page total.
 */
export interface LocationGroup {
  name: string;
  rows: string[][];   // one inner array per scene, ordered by visible header
  pageTotal: number;
}

export const exportLocationBreakdownToExcel = (
  title: string,
  headers: string[],
  groups: LocationGroup[],
  filename: string,
) => {
  const wb = XLSX.utils.book_new();
  const aoa: any[][] = [];

  const colCount = headers.length;
  const merges: { s: { r: number; c: number }; e: { r: number; c: number } }[] = [];

  // Document title row (project + breakdown name)
  aoa.push([title]);
  merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } });
  aoa.push([]); // spacer

  for (const group of groups) {
    const headerRowIdx = aoa.length;
    const totalLabel = group.pageTotal
      .toFixed(2)
      .replace(/\.00$/, '')
      .replace(/(\.\d)0$/, '$1');
    const groupHeader = `${group.name} — ${totalLabel} sayfa · ${group.rows.length} sahne`;
    aoa.push([groupHeader]);
    merges.push({
      s: { r: headerRowIdx, c: 0 },
      e: { r: headerRowIdx, c: colCount - 1 },
    });

    // Column headers row
    aoa.push(headers);

    // Scene rows
    for (const row of group.rows) {
      const padded = headers.map((_, i) => row[i] ?? '');
      aoa.push(padded);
    }

    // Blank spacer between groups
    aoa.push([]);
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!merges'] = merges;

  // Column widths — content-aware caps so the table reads nicely.
  ws['!cols'] = headers.map((h) => {
    const lower = h.toLowerCase();
    if (/sahne\s*no/.test(lower)) return { wch: 16 };
    if (/^mekan$/.test(lower) || /grubu/.test(lower)) return { wch: 32 };
    if (/zaman/.test(lower)) return { wch: 14 };
    if (/sayfa/.test(lower)) return { wch: 8 };
    if (/oyuncu/.test(lower)) return { wch: 28 };
    if (/yard[ıi]mc[ıi]/.test(lower)) return { wch: 22 };
    if (/özet|ozet/.test(lower)) return { wch: 60 };
    if (/aksesuar/.test(lower)) return { wch: 26 };
    if (/kost[üu]m/.test(lower)) return { wch: 18 };
    if (/araç|arac/.test(lower)) return { wch: 18 };
    if (/hayvan/.test(lower)) return { wch: 12 };
    if (/not/.test(lower)) return { wch: 22 };
    return { wch: 18 };
  });

  // Style: title + group headers bold, data rows wrap text. Note that
  // SheetJS community edition writes styles only for cell-format strings;
  // bold/colour rendering varies per spreadsheet app, so this is best-
  // effort and not a hard requirement for correctness.
  for (const merge of merges) {
    const ref = XLSX.utils.encode_cell({ r: merge.s.r, c: 0 });
    if (ws[ref]) {
      ws[ref].s = {
        font: { bold: true, sz: merge.s.r === 0 ? 14 : 12 },
        alignment: { horizontal: 'left', vertical: 'center' },
      };
    }
  }

  XLSX.utils.book_append_sheet(wb, ws, 'Mekan Dökümü');
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

/**
 * Production-style location breakdown — PDF export.
 * Lays out the same grouped tables as the Excel export but on landscape
 * pages, matching the visual rhythm of the user's reference document
 * (group header band, dense per-scene table, page totals on the right).
 */
export const exportLocationBreakdownToPDF = (
  title: string,
  headers: string[],
  groups: LocationGroup[],
  filename: string,
) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text(title, pageWidth / 2, 14, { align: 'center' });
  doc.setLineWidth(0.2);
  doc.line(12, 18, pageWidth - 12, 18);

  let nextY = 24;

  // Approximate column widths (mm) — biased toward the wide free-text
  // columns (Özet) and away from short ones (Sayfa, Hayvan).
  const widthHints: Record<string, number> = {
    'Sahne No': 18,
    Mekan: 38,
    Zaman: 22,
    Sayfa: 12,
    Oyuncular: 38,
    'Yardımcı Oyuncular': 28,
    Özet: 60,
    Aksesuar: 28,
    'Kostüm/Saç': 22,
    Araçlar: 22,
    Hayvan: 14,
    Notlar: 22,
  };
  const columnStyles: Record<number, any> = {};
  headers.forEach((h, i) => {
    columnStyles[i] = { cellWidth: widthHints[h] || 22 };
    if (/sahne\s*no/i.test(h)) columnStyles[i].fontStyle = 'bold';
    if (/sayfa/i.test(h)) columnStyles[i].halign = 'right';
  });

  for (const group of groups) {
    const totalLabel = group.pageTotal
      .toFixed(2)
      .replace(/\.00$/, '')
      .replace(/(\.\d)0$/, '$1');
    const headerText = `${group.name}  —  ${totalLabel} sayfa · ${group.rows.length} sahne`;

    // Group header band
    autoTable(doc, {
      startY: nextY,
      head: [[headerText]],
      body: [],
      theme: 'plain',
      styles: { fontSize: 11, font: 'helvetica', fontStyle: 'bold', cellPadding: 2 },
      headStyles: {
        fillColor: [244, 196, 48],
        textColor: [0, 0, 0],
        halign: 'left',
      },
      margin: { left: 12, right: 12 },
    });

    // Scene table
    autoTable(doc, {
      startY: (doc as any).lastAutoTable.finalY,
      head: [headers],
      body: group.rows.map((row) => headers.map((_, i) => row[i] ?? '')),
      theme: 'grid',
      styles: {
        fontSize: 7.5,
        font: 'helvetica',
        cellPadding: 1.5,
        overflow: 'linebreak',
        lineColor: [200, 200, 200],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [245, 245, 245],
        textColor: [60, 60, 60],
        fontStyle: 'bold',
        fontSize: 7,
      },
      columnStyles,
      margin: { left: 12, right: 12 },
    });

    nextY = (doc as any).lastAutoTable.finalY + 6;
  }

  doc.save(`${filename}.pdf`);
};

// PDF export fonksiyonu
export const exportToPDF = (
  data: any[],
  filename: string,
  columns: { header: string; dataKey: string }[],
  title: string = 'Report'
) => {
  const doc = new jsPDF();
  
  // Başlık ekle
  doc.setFontSize(16);
  doc.text(title, 14, 15);
  
  // Tarih ekle
  doc.setFontSize(10);
  doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);
  
  // Tablo ekle
  const tableData = data.map(row => 
    columns.map(col => row[col.dataKey] ?? '')
  );
  
  autoTable(doc, {
    head: [columns.map(col => col.header)],
    body: tableData,
    startY: 28,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [66, 139, 202] },
    alternateRowStyles: { fillColor: [245, 245, 245] },
  });
  
  doc.save(`${filename}.pdf`);
};

// Users export için özel fonksiyon
export const exportUsersToExcel = (users: any[]) => {
  const data = users.map(user => {
    const createdDate = user.created_at ? new Date(user.created_at) : null;
    const daysSinceCreation = createdDate 
      ? Math.floor((Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24))
      : null;
    const totalTokens = (user.tokens_remaining || 0) + (user.tokens_used || 0);
    const tokenUsagePercent = totalTokens > 0 
      ? ((user.tokens_used || 0) / totalTokens * 100).toFixed(1) + '%'
      : '0%';
    
    return {
      'Username': user.username,
      'Name': user.name || 'N/A',
      'Email': user.email || 'N/A',
      'Roles': (user.roles || []).join(', ') || 'user',
      'Total Projects': user.total_projects || 0,
      'Tokens Remaining': user.tokens_remaining || 0,
      'Tokens Used': user.tokens_used || 0,
      'Total Tokens': totalTokens,
      'Token Usage %': tokenUsagePercent,
      'Created At': createdDate ? createdDate.toLocaleString() : 'N/A',
      'Days Since Registration': daysSinceCreation !== null ? daysSinceCreation : 'N/A',
      'Is Admin': (user.roles || []).includes('admin') ? 'Yes' : 'No',
    };
  });
  exportToExcel(data, 'users_export', 'Users');
};

export const exportUsersToPDF = (users: any[]) => {
  const columns = [
    { header: 'Username', dataKey: 'username' },
    { header: 'Name', dataKey: 'name' },
    { header: 'Email', dataKey: 'email' },
    { header: 'Roles', dataKey: 'roles' },
    { header: 'Projects', dataKey: 'total_projects' },
    { header: 'Tokens Remaining', dataKey: 'tokens_remaining' },
    { header: 'Tokens Used', dataKey: 'tokens_used' },
    { header: 'Total Tokens', dataKey: 'total_tokens' },
    { header: 'Usage %', dataKey: 'token_usage_percent' },
    { header: 'Created At', dataKey: 'created_at' },
    { header: 'Days Active', dataKey: 'days_active' },
  ];
  
  const data = users.map(user => {
    const createdDate = user.created_at ? new Date(user.created_at) : null;
    const daysSinceCreation = createdDate 
      ? Math.floor((Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24))
      : null;
    const totalTokens = (user.tokens_remaining || 0) + (user.tokens_used || 0);
    const tokenUsagePercent = totalTokens > 0 
      ? ((user.tokens_used || 0) / totalTokens * 100).toFixed(1) + '%'
      : '0%';
    
    return {
      username: user.username,
      name: user.name || 'N/A',
      email: user.email || 'N/A',
      roles: (user.roles || []).join(', ') || 'user',
      total_projects: user.total_projects || 0,
      tokens_remaining: user.tokens_remaining || 0,
      tokens_used: user.tokens_used || 0,
      total_tokens: totalTokens,
      token_usage_percent: tokenUsagePercent,
      created_at: createdDate ? createdDate.toLocaleString() : 'N/A',
      days_active: daysSinceCreation !== null ? daysSinceCreation : 'N/A',
    };
  });
  
  exportToPDF(data, 'users_export', columns, 'Users Report');
};

// Global Statistics export
export const exportGlobalStatsToExcel = (stats: any) => {
  const totalTokens = (stats.total_tokens_remaining || 0) + (stats.total_tokens_used || 0);
  const tokenUsagePercent = totalTokens > 0 
    ? ((stats.total_tokens_used || 0) / totalTokens * 100).toFixed(2) + '%'
    : '0%';
  
  const data = [
    { 'Metric': 'Total Users', 'Value': stats.total_users || 0 },
    { 'Metric': 'Active Users', 'Value': stats.active_users || 0 },
    { 'Metric': 'Total Projects', 'Value': stats.total_projects || 0 },
    { 'Metric': 'Avg Projects per User', 'Value': stats.avg_projects_per_user?.toFixed(2) || '0.00' },
    { 'Metric': 'Total Tokens Remaining', 'Value': stats.total_tokens_remaining || 0 },
    { 'Metric': 'Total Tokens Used', 'Value': stats.total_tokens_used || 0 },
    { 'Metric': 'Total Tokens (All)', 'Value': totalTokens },
    { 'Metric': 'Token Usage Percentage', 'Value': tokenUsagePercent },
  ];
  exportToExcel(data, 'global_statistics', 'Global Statistics');
};

export const exportGlobalStatsToPDF = (stats: any) => {
  const columns = [
    { header: 'Metric', dataKey: 'metric' },
    { header: 'Value', dataKey: 'value' },
  ];
  
  const totalTokens = (stats.total_tokens_remaining || 0) + (stats.total_tokens_used || 0);
  const tokenUsagePercent = totalTokens > 0 
    ? ((stats.total_tokens_used || 0) / totalTokens * 100).toFixed(2) + '%'
    : '0%';
  
  const data = [
    { metric: 'Total Users', value: stats.total_users || 0 },
    { metric: 'Active Users', value: stats.active_users || 0 },
    { metric: 'Total Projects', value: stats.total_projects || 0 },
    { metric: 'Avg Projects per User', value: stats.avg_projects_per_user?.toFixed(2) || '0.00' },
    { metric: 'Total Tokens Remaining', value: stats.total_tokens_remaining || 0 },
    { metric: 'Total Tokens Used', value: stats.total_tokens_used || 0 },
    { metric: 'Total Tokens (All)', value: totalTokens },
    { metric: 'Token Usage Percentage', value: tokenUsagePercent },
  ];
  
  exportToPDF(data, 'global_statistics', columns, 'Global Statistics Report');
};

// User Statistics export
export const exportUserStatsToExcel = (stats: any, username: string) => {
  const data = [
    { 'Metric': 'Total Projects', 'Value': stats.total_projects || 0 },
    { 'Metric': 'Average Score', 'Value': stats.average_score?.toFixed(2) || '-' },
    { 'Metric': 'Highest Score', 'Value': stats.highest_score?.toFixed(2) || '-' },
    { 'Metric': 'Lowest Score', 'Value': stats.lowest_score?.toFixed(2) || '-' },
  ];
  exportToExcel(data, `user_stats_${username}`, 'User Statistics');
};

export const exportUserStatsToPDF = (stats: any, username: string) => {
  const columns = [
    { header: 'Metric', dataKey: 'metric' },
    { header: 'Value', dataKey: 'value' },
  ];
  
  const data = [
    { metric: 'Total Projects', value: stats.total_projects || 0 },
    { metric: 'Average Score', value: stats.average_score?.toFixed(2) || '-' },
    { metric: 'Highest Score', value: stats.highest_score?.toFixed(2) || '-' },
    { metric: 'Lowest Score', value: stats.lowest_score?.toFixed(2) || '-' },
  ];
  
  exportToPDF(data, `user_stats_${username}`, columns, `User Statistics - ${username}`);
};

// Token History export
export const exportTokenHistoryToExcel = (history: any[]) => {
  const data = history.map(tx => {
    const date = new Date(tx.created_at);
    return {
      'Date': date.toLocaleString(),
      'Date (ISO)': date.toISOString(),
      'Username': tx.username || 'N/A',
      'Type': tx.transaction_type === 'purchase' ? 'Purchase' : 'Usage',
      'Amount': tx.amount,
      'Amount (Formatted)': tx.amount > 0 ? `+${tx.amount}` : String(tx.amount),
      'Balance Before': tx.balance_before,
      'Balance After': tx.balance_after,
      'Change': tx.amount > 0 ? 'Increase' : 'Decrease',
    };
  });
  exportToExcel(data, 'token_history', 'Token History');
};

export const exportTokenHistoryToPDF = (history: any[]) => {
  const columns = [
    { header: 'Date', dataKey: 'date' },
    { header: 'Username', dataKey: 'username' },
    { header: 'Type', dataKey: 'type' },
    { header: 'Amount', dataKey: 'amount' },
    { header: 'Balance Before', dataKey: 'balance_before' },
    { header: 'Balance After', dataKey: 'balance_after' },
  ];
  
  const data = history.map(tx => ({
    date: new Date(tx.created_at).toLocaleString(),
    username: tx.username || 'N/A',
    type: tx.transaction_type === 'purchase' ? 'Purchase' : 'Usage',
    amount: tx.amount > 0 ? `+${tx.amount}` : String(tx.amount),
    balance_before: tx.balance_before,
    balance_after: tx.balance_after,
  }));
  
  exportToPDF(data, 'token_history', columns, 'Token History Report');
};

// Analytics export
export const exportAnalyticsToExcel = (analytics: any) => {
  const workbook = XLSX.utils.book_new();
  
  // Key Metrics
  const totalTokens = (analytics.total_tokens_consumed || 0) + (analytics.total_tokens_remaining || 0);
  const tokenUsagePercent = totalTokens > 0 
    ? ((analytics.total_tokens_consumed || 0) / totalTokens * 100).toFixed(2) + '%'
    : '0%';
  
  const metricsData = [
    { 'Metric': 'Total Users', 'Value': analytics.total_users || 0 },
    { 'Metric': 'Active Users (30d)', 'Value': analytics.active_users_30d || 0 },
    { 'Metric': 'Total Projects', 'Value': analytics.total_projects || 0 },
    { 'Metric': 'Total Tokens Consumed', 'Value': analytics.total_tokens_consumed || 0 },
    { 'Metric': 'Total Tokens Remaining', 'Value': analytics.total_tokens_remaining || 0 },
    { 'Metric': 'Total Tokens (All)', 'Value': totalTokens },
    { 'Metric': 'Token Usage Percentage', 'Value': tokenUsagePercent },
    { 'Metric': 'Average Score', 'Value': analytics.avg_score?.toFixed(2) || 0 },
    { 'Metric': 'Avg Session Duration (seconds)', 'Value': analytics.avg_session_duration || 0 },
    { 'Metric': 'Avg Session Duration (minutes)', 'Value': analytics.avg_session_duration ? Math.floor(analytics.avg_session_duration / 60).toFixed(1) : 0 },
  ];
  const metricsSheet = XLSX.utils.json_to_sheet(metricsData);
  XLSX.utils.book_append_sheet(workbook, metricsSheet, 'Key Metrics');
  
  // Activity by Type
  if (analytics.activity_by_type) {
    const activityData = analytics.activity_by_type.map((a: any) => ({
      'Activity Type': a.activity_type,
      'Count': a.count,
    }));
    const activitySheet = XLSX.utils.json_to_sheet(activityData);
    XLSX.utils.book_append_sheet(workbook, activitySheet, 'Activity by Type');
  }
  
  // Top Features
  if (analytics.top_features) {
    const featuresData = analytics.top_features.map((f: any) => ({
      'Feature Name': f.feature_name,
      'Total Uses': f.total_uses,
      'Total Tokens': f.total_tokens,
    }));
    const featuresSheet = XLSX.utils.json_to_sheet(featuresData);
    XLSX.utils.book_append_sheet(workbook, featuresSheet, 'Top Features');
  }
  
  // Language Distribution
  if (analytics.language_distribution) {
    const langData = analytics.language_distribution.map((l: any) => ({
      'Language': l.report_language,
      'Count': l.count,
    }));
    const langSheet = XLSX.utils.json_to_sheet(langData);
    XLSX.utils.book_append_sheet(workbook, langSheet, 'Language Distribution');
  }
  
  // Project Type Distribution
  if (analytics.project_type_distribution) {
    const typeData = analytics.project_type_distribution.map((t: any) => ({
      'Project Type': t.project_type,
      'Count': t.count,
    }));
    const typeSheet = XLSX.utils.json_to_sheet(typeData);
    XLSX.utils.book_append_sheet(workbook, typeSheet, 'Project Type Distribution');
  }
  
  // Country Distribution
  if (analytics.country_distribution) {
    const countryData = analytics.country_distribution.map((c: any) => ({
      'Country': c.country || 'Unknown',
      'Count': c.count,
    }));
    const countrySheet = XLSX.utils.json_to_sheet(countryData);
    XLSX.utils.book_append_sheet(workbook, countrySheet, 'Country Distribution');
  }
  
  // New Users by Month
  if (analytics.new_users_by_month) {
    const monthData = analytics.new_users_by_month.map((m: any) => ({
      'Month': m.month,
      'New Users': m.new_users,
    }));
    const monthSheet = XLSX.utils.json_to_sheet(monthData);
    XLSX.utils.book_append_sheet(workbook, monthSheet, 'New Users by Month');
  }
  
  XLSX.writeFile(workbook, 'analytics_export.xlsx');
};

export const exportAnalyticsToPDF = (analytics: any) => {
  const doc = new jsPDF();
  let yPos = 15;
  
  const addPageIfNeeded = () => {
    if (yPos > 250) {
      doc.addPage();
      yPos = 15;
    }
  };
  
  // Başlık
  doc.setFontSize(16);
  doc.text('Analytics Report', 14, yPos);
  yPos += 10;
  
  doc.setFontSize(10);
  doc.text(`Generated: ${new Date().toLocaleString()}`, 14, yPos);
  yPos += 15;
  
  // Key Metrics
  doc.setFontSize(12);
  doc.text('Key Metrics', 14, yPos);
  yPos += 8;
  
  const totalTokens = (analytics.total_tokens_consumed || 0) + (analytics.total_tokens_remaining || 0);
  const tokenUsagePercent = totalTokens > 0 
    ? ((analytics.total_tokens_consumed || 0) / totalTokens * 100).toFixed(2) + '%'
    : '0%';
  
  const metricsData = [
    ['Metric', 'Value'],
    ['Total Users', String(analytics.total_users || 0)],
    ['Active Users (30d)', String(analytics.active_users_30d || 0)],
    ['Total Projects', String(analytics.total_projects || 0)],
    ['Total Tokens Consumed', String(analytics.total_tokens_consumed || 0)],
    ['Total Tokens Remaining', String(analytics.total_tokens_remaining || 0)],
    ['Total Tokens (All)', String(totalTokens)],
    ['Token Usage %', tokenUsagePercent],
    ['Average Score', analytics.avg_score?.toFixed(2) || '0'],
    ['Avg Session Duration (min)', analytics.avg_session_duration ? Math.floor(analytics.avg_session_duration / 60).toFixed(1) : '0'],
  ];
  
  autoTable(doc, {
    head: [metricsData[0]],
    body: metricsData.slice(1),
    startY: yPos,
    styles: { fontSize: 9 },
    headStyles: { fillColor: [66, 139, 202] },
  });
  
  yPos = (doc as any).lastAutoTable?.finalY ? (doc as any).lastAutoTable.finalY + 15 : yPos + 30;
  
  // Activity by Type
  if (analytics.activity_by_type && analytics.activity_by_type.length > 0) {
    addPageIfNeeded();
    
    doc.setFontSize(12);
    doc.text('Activity by Type', 14, yPos);
    yPos += 8;
    
    const activityData = analytics.activity_by_type.map((a: any) => [
      a.activity_type.replace('_', ' '),
      String(a.count),
    ]);
    
    autoTable(doc, {
      head: [['Activity Type', 'Count']],
      body: activityData,
      startY: yPos,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [66, 139, 202] },
    });
    
    yPos = (doc as any).lastAutoTable?.finalY ? (doc as any).lastAutoTable.finalY + 15 : yPos + 30;
  }
  
  // Top Features
  if (analytics.top_features && analytics.top_features.length > 0) {
    addPageIfNeeded();
    
    doc.setFontSize(12);
    doc.text('Top Features', 14, yPos);
    yPos += 8;
    
    const featuresData = analytics.top_features.map((f: any) => [
      f.feature_name.replace('_', ' '),
      String(f.total_uses),
      String(f.total_tokens),
    ]);
    
    autoTable(doc, {
      head: [['Feature Name', 'Total Uses', 'Total Tokens']],
      body: featuresData,
      startY: yPos,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [66, 139, 202] },
    });
    
    yPos = (doc as any).lastAutoTable?.finalY ? (doc as any).lastAutoTable.finalY + 15 : yPos + 30;
  }
  
  // Language Distribution
  if (analytics.language_distribution && analytics.language_distribution.length > 0) {
    addPageIfNeeded();
    
    doc.setFontSize(12);
    doc.text('Language Distribution', 14, yPos);
    yPos += 8;
    
    const langData = analytics.language_distribution.map((l: any) => [
      l.report_language,
      String(l.count),
    ]);
    
    autoTable(doc, {
      head: [['Language', 'Count']],
      body: langData,
      startY: yPos,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [66, 139, 202] },
    });
    
    yPos = (doc as any).lastAutoTable?.finalY ? (doc as any).lastAutoTable.finalY + 15 : yPos + 30;
  }
  
  // Project Type Distribution
  if (analytics.project_type_distribution && analytics.project_type_distribution.length > 0) {
    addPageIfNeeded();
    
    doc.setFontSize(12);
    doc.text('Project Type Distribution', 14, yPos);
    yPos += 8;
    
    const typeData = analytics.project_type_distribution.map((t: any) => [
      t.project_type,
      String(t.count),
    ]);
    
    autoTable(doc, {
      head: [['Project Type', 'Count']],
      body: typeData,
      startY: yPos,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [66, 139, 202] },
    });
    
    yPos = (doc as any).lastAutoTable?.finalY ? (doc as any).lastAutoTable.finalY + 15 : yPos + 30;
  }
  
  // Country Distribution
  if (analytics.country_distribution && analytics.country_distribution.length > 0) {
    addPageIfNeeded();
    
    doc.setFontSize(12);
    doc.text('Country Distribution (30 Days)', 14, yPos);
    yPos += 8;
    
    const countryData = analytics.country_distribution.map((c: any) => [
      c.country || 'Unknown',
      String(c.count),
    ]);
    
    autoTable(doc, {
      head: [['Country', 'Count']],
      body: countryData,
      startY: yPos,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [66, 139, 202] },
    });
    
    yPos = (doc as any).lastAutoTable?.finalY ? (doc as any).lastAutoTable.finalY + 15 : yPos + 30;
  }
  
  // New Users by Month
  if (analytics.new_users_by_month && analytics.new_users_by_month.length > 0) {
    addPageIfNeeded();
    
    doc.setFontSize(12);
    doc.text('New Users by Month', 14, yPos);
    yPos += 8;
    
    const monthData = analytics.new_users_by_month.map((m: any) => [
      m.month,
      String(m.new_users),
    ]);
    
    autoTable(doc, {
      head: [['Month', 'New Users']],
      body: monthData,
      startY: yPos,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [66, 139, 202] },
    });
  }
  
  doc.save('analytics_export.pdf');
};

